// @givmo/board — the officer's calendar file while Board keeps a meeting in a calendar (CBG-CAL1
// slice 1d; question 10, answered "yes" 2026-10-05 22:45 PDT with Jonathan's refinement).
//
// A downloaded file beside an invitation makes a second entry in the same calendar — the reason the
// director's page already drops it. So while Board keeps the meeting in a calendar (its link is live:
// `pending`, `sent`, `needs_attention`, `cancelled`), "Add to calendar" — and a cancelled meeting's
// "Update your calendar" — is hidden ONLY from someone who already holds the invitation:
//   * the account it is sent from: a connection the viewer owns (core's list: `owner_user_id` is the
//     viewer, its address the block's `organizer_email`);
//   * or an invited seat: one of the viewer's seats (the roster's `user_id`) among the meeting's live
//     invitees (the invitations read: `board_member_id`, no `removed_at`).
// Everyone else keeps it — a staff member who schedules meetings but isn't on the roster. With no
// block, or a detached one, everyone keeps it, as today.
//
// While a kept meeting's reads are in flight the answer is `null` (unknown), and the file waits for
// it rather than flash and vanish. A viewer the host can't identify keeps the file: the rule hides it
// only from someone known to hold the invitation.

import { useQuery } from '@tanstack/react-query';
import type { OrgBoardMeetingOut, OrgBoardMemberOut } from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { useBoardHost } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { useMeetingInvitations, useOrgConnections } from '../../calendar/calendar-queries';
import { decodeMeetingCalendar, isKeptInCalendar } from '../meetings/calendar-decode';

/** True: the viewer already holds the invitation. False: they don't (or nothing is kept). Null: not yet known. */
export function useHoldsInvitation(meeting: OrgBoardMeetingOut | null): boolean | null {
  const { orgRef, userId } = useBoardHost();
  const operations = useBoardOperations();
  const block = decodeMeetingCalendar((meeting as { calendar?: unknown } | null)?.calendar);
  const kept =
    meeting !== null && isKeptInCalendar(block) && userId !== null && userId !== undefined;
  const connected = kept && block?.channel === 'org_calendar';
  const connections = useOrgConnections(orgRef, connected);
  const invitations = useMeetingInvitations(orgRef, meeting?.id ?? '', kept);
  const seats = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: kept && orgRef !== null,
    select: (body: unknown) => extractList<OrgBoardMemberOut>(body, 'data'),
  });
  if (!kept || block === null) return false;

  const organizer = (block.organizer_email ?? '').trim().toLowerCase();
  const sendsIt =
    connected &&
    (connections.data ?? []).some(
      (row) =>
        row.owner_user_id === userId &&
        row.status !== 'disconnected' &&
        row.account_email.trim().toLowerCase() === organizer,
    );
  if (sendsIt) return true;

  const mySeats = new Set(
    (seats.data ?? []).filter((seat) => seat.user_id === userId).map((seat) => seat.id),
  );
  const invited = (invitations.data?.invitees ?? []).some(
    (invitee) => invitee.removed_at === null && mySeats.has(invitee.board_member_id),
  );
  if (invited) return true;

  const settled =
    (!connected || !connections.isPending) && !invitations.isPending && !seats.isPending;
  return settled ? false : null;
}
